import { queryOptions } from "@tanstack/react-query";
import type {
  CreateDocumentTypeInput,
  DocumentTypeDto,
  DocumentTypeId,
  PaginatedResponse,
  UpdateDocumentTypeInput,
} from "@yalana/contracts";
import { fetchApi } from "../../../../shared/infrastructure/api-client";
import { toListSearchParams } from "../../../../shared/utils/list-search-params";

export interface DocumentTypeListParams {
  customerId?: string;
  page?: number;
  pageSize?: number;
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
  [key: string]: string | number | boolean | null | undefined;
}

export const documentTypesQueries = {
  all: () => ["fsm", "document-types"] as const,
  list: (params: DocumentTypeListParams = {}) =>
    queryOptions({
      queryKey: [...documentTypesQueries.all(), "list", params] as const,
      queryFn: () =>
        fetchApi<PaginatedResponse<DocumentTypeDto>>(
          "/api/fsm/document-types",
          { searchParams: toListSearchParams(params) },
        ),
    }),
  forUser: (customerId?: string) =>
    queryOptions({
      queryKey: [
        ...documentTypesQueries.all(),
        "for-user",
        customerId,
      ] as const,
      queryFn: () => {
        const url = customerId
          ? `/api/fsm/document-types/for-user?customerId=${customerId}`
          : "/api/fsm/document-types/for-user";
        return fetchApi<DocumentTypeDto[]>(url);
      },
    }),
  detail: (id: DocumentTypeId) =>
    queryOptions({
      queryKey: [...documentTypesQueries.all(), id] as const,
      queryFn: () => fetchApi<DocumentTypeDto>(`/api/fsm/document-types/${id}`),
      enabled: id !== ("new" as unknown as DocumentTypeId),
    }),
};

export const documentTypesMutations = {
  create: () => ({
    mutationFn: (data: CreateDocumentTypeInput) => {
      const { customerId, ...body } = data;
      return fetchApi<DocumentTypeDto>(
        `/api/fsm/document-types?customerId=${customerId}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
  }),
  update: (id: DocumentTypeId) => ({
    mutationFn: (data: UpdateDocumentTypeInput) =>
      fetchApi<DocumentTypeDto>(`/api/fsm/document-types/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  }),
  delete: () => ({
    mutationFn: (id: DocumentTypeId) =>
      fetchApi<void>(`/api/fsm/document-types/${id}`, { method: "DELETE" }),
  }),
};
